import { formatNumber } from "@memorang/helpers/common";
import type Icon from "@react-native-vector-icons/material-design-icons/static";
import { useTranslation } from "react-i18next";
import Box from "../Box";
import { CustomListSubheader } from "../CustomListSubheader";
import OptionListItem from "../QuickStudyModal/OptionListItem";

type LearningMode = {
	title: string;
	icon: React.ComponentProps<typeof Icon>["name"];
	value: string;
	disabled?: boolean;
	count: number;
};

type NumItemsMap = Record<string, number>;

type Props = {
	selectedLearningMode: string;
	handleSetLearningMode: (learningMode: string) => void;
	numItemsMap: NumItemsMap;
	disableAll?: boolean;
};

const getLearningModes = (
	_numFlashcards: number,
	numQuestions: number,
	numEssays?: number,
): LearningMode[] => [
	{
		title: "Questions",
		icon: "order-bool-descending",
		value: "question",
		disabled: !numQuestions,
		count: numQuestions,
	},
	...(numEssays
		? ([
				{
					title: "Essays",
					icon: "file-document-edit-outline",
					value: "essay",
					disabled: !numEssays,
					count: numEssays,
				},
			] satisfies LearningMode[])
		: []),
];

const ItemTypesSelector = ({
	selectedLearningMode,
	handleSetLearningMode,
	disableAll,
	numItemsMap,
}: Props) => {
	const { t } = useTranslation();
	const { numFlashcards = 0, numQuestions = 0, numEssays } = numItemsMap;
	const learningModes = getLearningModes(
		numFlashcards,
		numQuestions,
		numEssays,
	);

	return (
		<Box gap={8}>
			<CustomListSubheader enableGutters={false} style={{ paddingVertical: 0 }}>
				Item types
			</CustomListSubheader>
			{learningModes.map(({ title, value, disabled, icon, count }) => {
				const selected = value === selectedLearningMode;
				return (
					<OptionListItem
						key={title}
						id={value}
						label={title}
						disabled={disabled || disableAll}
						icon={icon}
						countLabel={`${formatNumber(count)} ${t("common.item", { count })}`}
						checked={selected}
						selectionVariant="radio"
						onToggle={handleSetLearningMode}
					/>
				);
			})}
		</Box>
	);
};

export default ItemTypesSelector;
